import { useDisclosure } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  CONTENT_INNER_PX_SIZE,
  HEADER_ID,
  SIDEBAR_ID,
} from '@/components/templates/PageLayout';
import {
  CmBaseTableWithQuestionIcon,
  CmBox,
  CmCard,
  CmFlex,
  CmHeadingPagination,
  CmLabelAndInput,
  CmLink,
  CmLongLoading,
  CmMessageCard,
  CmScrollBox,
  CmTableBodyRow,
  CmTd,
  CmText,
  CmTooltip,
  ExternalLinkIcon,
  useCounter,
} from '@/components/uiParts';
import { LIMIT_PER_PAGE } from '@/domain/models/Pagination';
import { useTargetsDomHeight, useTargetsDomWidth } from '@/hooks/Dom';
import { canUseStoreProxyLogin, redirect404Page } from '@/lib/AccessControl';
import { ommisionText } from '@/lib/util';
import { AuthStoreSidebarForm } from '@/pages/store-management/auth-stores/components/AuthStoreSidebarForm';
import {
  authStoreForm,
  authStoreTableThs,
  storeProxyLoginTh,
  storeProxyLoginUrl,
} from '@/pages/store-management/auth-stores/service';
import { StoreManegementPageLayout } from '@/pages/store-management/components/Layout/StoreManegementPageLayout';
import { routes } from '@/router';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import {
  StoreManagementAuthStoreShowReqponse,
  StoreManagementSearchList,
  useStoreManagementAuthStoreSearchList,
  useStoreManagementAuthStoreShow,
} from '@/services/StoreManagement/AuthStores/hooks';
export const SEARCH_AREA_ID = 'search-form';

// 店舗情報変更の実装が全て完了するまで、Stores.tsxを残しておきたいので新しいファイルを作成して実装を行う
const StoreManegementAuthStoresPage: React.FC = (): JSX.Element | null => {
  const history = useHistory();
  const { managerAccountMe } = useManagerAccountMe();
  // /* アクセス制御 */
  redirect404Page('store_management', history, managerAccountMe);
  const isStoreProxyLoginVisible = canUseStoreProxyLogin(managerAccountMe);
  const tableThs = isStoreProxyLoginVisible
    ? [...authStoreTableThs, storeProxyLoginTh]
    : authStoreTableThs;
  const [authStores, setAuthStores] = useState<StoreManagementSearchList[]>([]);
  const [sidebarAuthStoreId, setSidebarAuthStoreId] = useState<number>(0);
  const [name, setName] = useState<string>('');
  const [searchCount, setSearchCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { count, decrement, increment, customizeCount } = useCounter(1);
  const [timer, setTimer] = useState<number>();
  const {
    isOpen: isSidebarOpen,
    onOpen: handleOpenSidebar,
    onClose: handleCloseSidebar,
  } = useDisclosure();
  const { targetsHeight } = useTargetsDomHeight([HEADER_ID, SEARCH_AREA_ID]);
  const getListQuery = {
    currentPage: 1,
    limit: LIMIT_PER_PAGE,
    name,
  };
  const [targetAuthStore, setTargetAuthStore] =
    useState<StoreManagementAuthStoreShowReqponse>();
  const handleInputName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setName(name);
    clearTimeout(Number(timer));
    const newTimer = setTimeout(async () => {
      setIsLoading(true);
      const { auth_stores, count } =
        await useStoreManagementAuthStoreSearchList({
          ...getListQuery,
          currentPage: 1,
          name,
        });
      customizeCount(1);
      setSearchCount(count);
      setAuthStores(auth_stores);
      setIsLoading(false);
    }, 700);
    setTimer(Number(newTimer));
  };

  /* ページネーション */
  const toFirstPage = async () => {
    setIsLoading(true);
    const { auth_stores } = await useStoreManagementAuthStoreSearchList({
      ...getListQuery,
    });
    setAuthStores(auth_stores), customizeCount(1), setIsLoading(false);
  };

  const toPreviousPage = async () => {
    setIsLoading(true);
    const previousPage = count - 1;
    const { auth_stores } = await useStoreManagementAuthStoreSearchList({
      ...getListQuery,
      currentPage: previousPage,
    });
    setAuthStores(auth_stores), decrement(), setIsLoading(false);
  };

  const toNextPage = async () => {
    setIsLoading(true);
    const nextPage = count + 1;
    const { auth_stores } = await useStoreManagementAuthStoreSearchList({
      ...getListQuery,
      currentPage: nextPage,
    });
    setAuthStores(auth_stores), increment(), setIsLoading(false);
  };

  const toLastPage = async () => {
    setIsLoading(true);
    const lastPage = Math.ceil(searchCount / LIMIT_PER_PAGE);
    const { auth_stores } = await useStoreManagementAuthStoreSearchList({
      ...getListQuery,
      currentPage: lastPage,
    });
    setAuthStores(auth_stores), customizeCount(lastPage), setIsLoading(false);
  };
  const handleOpenSideBar = async (authStoreId: number) => {
    const targetAuthStore = await useStoreManagementAuthStoreShow(authStoreId);
    setTargetAuthStore(targetAuthStore);
    setSidebarAuthStoreId(authStoreId), handleOpenSidebar();
  };

  const handleInitializeAuthStores = async () => {
    handleCloseSidebar();
    const { auth_stores, count } = await useStoreManagementAuthStoreSearchList(
      getListQuery
    );
    setAuthStores(auth_stores);
    setSearchCount(count);
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const { auth_stores, count } =
        await useStoreManagementAuthStoreSearchList({
          ...getListQuery,
        });
      customizeCount(1);
      setAuthStores(auth_stores);
      setSearchCount(count);
      setIsLoading(false);
    })();
  }, []);

  const { targetsWidth: sidebarWidth } = useTargetsDomWidth([SIDEBAR_ID]);
  const formComponsent = (
    <AuthStoreSidebarForm
      {...{
        sidebarAuthStoreId,
        targetAuthStore,
        handleCloseSidebar,
        isSidebarOpen,
        handleInitializeAuthStores,
      }}
    />
  );

  return (
    <StoreManegementPageLayout
      title={routes.storeManagementAuthStores.title}
      {...{
        formComponsent,
      }}
    >
      <CmBox>
        <CmBox padding="20px 20px 20px 0" id={SEARCH_AREA_ID}>
          <CmFlex alignItems="flex-end" justifyContent="space-between" w="full">
            <CmFlex alignItems="flex-end" width="full">
              <CmBox width="40%">
                <CmLabelAndInput
                  bg="white"
                  id="name"
                  label={authStoreForm.name.label}
                  fontWeight="bold"
                  value={name}
                  onChange={handleInputName}
                />
              </CmBox>
            </CmFlex>
            <CmBox width="40%" textAlign="right">
              件数: {searchCount} 件
            </CmBox>
          </CmFlex>
        </CmBox>

        {authStores.length > 0 ? (
          <CmCard
            padding="20px"
            height={`calc(100vh - ${targetsHeight}px - 20px)`}
            w={`calc(100vw - ${sidebarWidth + CONTENT_INNER_PX_SIZE * 2}px)`}
          >
            <CmFlex flexDirection="row-reverse" my="5px">
              <CmHeadingPagination
                totalNum={searchCount}
                currentPage={count}
                LimitPerPage={LIMIT_PER_PAGE}
                {...{ toFirstPage, toNextPage, toPreviousPage, toLastPage }}
              />
            </CmFlex>
            <CmScrollBox>
              <CmBaseTableWithQuestionIcon ths={tableThs}>
                {authStores.map((authStore, index) => {
                  const {
                    id,
                    name,
                    email,
                    prefecture,
                    city,
                    phone_number,
                    street_address,
                    building,
                    latitude,
                    longitude,
                  } = authStore;
                  const address =
                    prefecture + city + street_address + (building ?? '');
                  return (
                    <CmTableBodyRow key={index + 1}>
                      <CmTd>
                        <CmBox
                          tabIndex={0}
                          cursor="pointer"
                          padding="5px"
                          onClick={async () => await handleOpenSideBar(id)}
                        >
                          <CmText color="blue.500">
                            <CmTooltip label={name}>
                              {ommisionText(name, 20)}
                            </CmTooltip>
                          </CmText>
                        </CmBox>
                      </CmTd>
                      <CmTd>
                        <CmTooltip label={email}>
                          {ommisionText(email, 20)}
                        </CmTooltip>
                      </CmTd>
                      <CmTd textAlign="center">{phone_number}</CmTd>
                      <CmTd textAlign="center">
                        <CmTooltip label={address}>
                          {ommisionText(address, 25)}
                        </CmTooltip>
                      </CmTd>
                      <CmTd textAlign="center">{latitude?.toFixed(2)}</CmTd>
                      <CmTd textAlign="center">{longitude?.toFixed(2)}</CmTd>
                      {isStoreProxyLoginVisible && (
                        <CmTd textAlign="center">
                          <CmLink
                            isExternal
                            to={storeProxyLoginUrl(id)}
                            color="blue.500"
                          >
                            <ExternalLinkIcon mr="2px" mt="-2px" />
                            タブレット画面を開く
                          </CmLink>
                        </CmTd>
                      )}
                    </CmTableBodyRow>
                  );
                })}
              </CmBaseTableWithQuestionIcon>
            </CmScrollBox>
          </CmCard>
        ) : (
          <>
            {!isLoading && (
              <CmMessageCard margin="100px auto 0">
                検索結果に一致する拠点が見当たりませんでした。
              </CmMessageCard>
            )}
          </>
        )}
        {isLoading && <CmLongLoading />}
      </CmBox>
    </StoreManegementPageLayout>
  );
};

export default StoreManegementAuthStoresPage;
